// ===== 1. Imports =====
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Eye,
  Pencil,
  Trash,
  AlertTriangle,
  AlertOctagon,
  CheckCircle2,
  Upload,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  FileWarning,
  Globe,
  BadgeEuro,
  PlusCircle,
} from "lucide-react";
import Modal from "../components/Modal";
import { db, storage } from "../firebaseConfig";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

// ===== 2. Utilidades =====
function initialForm() {
  return {
    name: "",
    type: "ES", // ES, EU, INT
    cif: "",
    address: "",
    postalCode: "",
    city: "",
    province: "",
    country: "",
    paymentMethod: "transferencia",
    iban: "",
    certBank: { fileUrl: "", fileName: "", expiry: "" },
    certAeat: { fileUrl: "", fileName: "", expiry: "" },
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    website: "",
    code: null, // se asigna al crear
    createdAt: null,
    updatedAt: null,
  };
}

const formatSupplierCode = (num) => num?.toString().padStart(3, "0") || "000";

const todayISO = () => new Date().toISOString().slice(0, 10);
const isPast = (isoDate) =>
  !!isoDate && new Date(isoDate) < startOfDay(new Date());
const isNear = (isoDate, days = 30) => {
  if (!isoDate) return false;
  const d = new Date(isoDate);
  const now = startOfDay(new Date());
  const limit = new Date(now);
  limit.setDate(limit.getDate() + days);
  return d >= now && d <= limit;
};
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

// Validaciones básicas (no exhaustivas)
const validateIBAN = (iban = "") => {
  const i = iban.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/.test(i)) return false;
  // Rearrange
  const moved = i.slice(4) + i.slice(0, 4);
  // Convert letters
  const numStr = moved.replace(/[A-Z]/g, (ch) => ch.charCodeAt(0) - 55);
  // Mod 97
  let remainder = 0n;
  for (let idx = 0; idx < numStr.length; idx += 9) {
    const part = numStr.slice(idx, idx + 9);
    remainder = (remainder * 10n ** BigInt(part.length) + BigInt(part)) % 97n;
  }
  return remainder === 1n;
};

const validateCIFBasic = (cif = "") =>
  /^[A-Za-z0-9][0-9]{7}[A-Za-z0-9]$/.test(cif.replace(/\s+/g, ""));

// Estado de certificados
function computeCertStatus(supplier) {
  const b = supplier?.certBank || {};
  const a = supplier?.certAeat || {};

  const missingBank = !b.fileUrl;
  const missingAeat = !a.fileUrl;

  const expiredBank = isPast(b.expiry);
  const expiredAeat = isPast(a.expiry);

  const nearBank = isNear(b.expiry);
  const nearAeat = isNear(a.expiry);

  // Prioridad rojo (vencido) > naranja (faltante o próximo a vencer) > verde
  if (expiredBank || expiredAeat) {
    return { level: "red", reason: "Certificado vencido" };
  }
  if (missingBank || missingAeat || nearBank || nearAeat) {
    return {
      level: "orange",
      reason: missingBank || missingAeat ? "Falta certificado" : "Vence pronto",
    };
  }
  return { level: "green", reason: "OK" };
}

// ===== 3. Componentes internos =====
function BadgeType({ type }) {
  const map = {
    ES: { label: "ES", bg: "#eef2ff", fg: "#3730a3", Icon: BadgeEuro },
    EU: { label: "EU", bg: "#ecfeff", fg: "#155e75", Icon: Globe },
    INT: { label: "INT", bg: "#ecfdf5", fg: "#065f46", Icon: Globe },
  };
  const cfg = map[type] || map.ES;
  const Icon = cfg.Icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 8px",
        borderRadius: 999,
        background: cfg.bg,
        color: cfg.fg,
        fontSize: 12,
        fontWeight: 600,
      }}
      title={`Proveedor ${cfg.label}`}
    >
      <Icon size={14} /> {cfg.label}
    </span>
  );
}

function StatusDot({ status }) {
  const { level, reason } = status || {};
  const color =
    level === "red" ? "#dc2626" : level === "orange" ? "#f59e0b" : "#22c55e";
  const Icon =
    level === "red"
      ? AlertOctagon
      : level === "orange"
      ? AlertTriangle
      : CheckCircle2;
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      title={reason}
      aria-label={reason}
    >
      <Icon size={16} color={color} />
    </span>
  );
}

function SupplierCard({ s, onView, onEdit, onDelete }) {
  const status = computeCertStatus(s);
  return (
    <div className="card" style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <h3
          style={{
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          <span
            style={{
              background: "#e5e7eb",
              color: "#111827",
              fontFamily: "monospace",
              padding: "3px 8px",
              borderRadius: 6,
              fontSize: 13,
              whiteSpace: "nowrap",
            }}
          >
            {formatSupplierCode(s.code)}
          </span>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 380,
              display: "inline-block",
            }}
            title={s.name}
          >
            {s.name}
          </span>
          <BadgeType type={s.type} />
          <StatusDot status={status} />
        </h3>

        <div style={{ display: "flex", gap: 6 }}>
          <button className="button button--ghost" onClick={() => onView(s)}>
            <Eye size={16} />
          </button>
          <button className="button button--ghost" onClick={() => onEdit(s)}>
            <Pencil size={16} />
          </button>
          <button className="button button--ghost" onClick={() => onDelete(s)}>
            <Trash size={16} />
          </button>
        </div>
      </div>

      <div
        style={{ fontSize: 14, marginTop: 6, color: "var(--muted-foreground)" }}
      >
        CIF: {s.cif || "—"} • {s.city || "sin ciudad"} •{" "}
        {s.country || "sin país"}
      </div>
    </div>
  );
}

function CertDropzone({
  label,
  value,
  onFileSelected,
  onRemoveFile,
  expiry,
  onExpiryChange,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const expired = isPast(expiry);
  const near = isNear(expiry);
  const border = expired
    ? "2px dashed #dc2626"
    : near
    ? "2px dashed #f59e0b"
    : "2px dashed var(--ring)";

  return (
    <div className="field field--span2">
      <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {label}
        {expired && <StatusDot status={{ level: "red", reason: "Vencido" }} />}
        {!expired && near && (
          <StatusDot status={{ level: "orange", reason: "Vence pronto" }} />
        )}
      </label>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) onFileSelected(f);
        }}
        onClick={() => inputRef.current?.click()}
        style={{
          border,
          borderRadius: 10,
          padding: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          background: dragOver ? "rgba(0,0,0,0.03)" : "transparent",
          cursor: "pointer",
        }}
        title="Haz click o arrastra un archivo"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Upload size={18} />
          <div>
            <div style={{ fontWeight: 600 }}>
              {value?.fileName || "Arrastra o haz click para seleccionar"}
            </div>
            <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              PDF/JPG/PNG • Máx ~10 MB
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {value?.fileUrl && (
            <>
              <a
                href={value.fileUrl}
                className="button"
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="Descargar / Abrir"
              >
                <Download size={16} />
              </a>
              <button
                className="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFile();
                }}
                title="Eliminar certificado"
              >
                <X size={16} />
              </button>
            </>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFileSelected(f);
            e.target.value = "";
          }}
        />
      </div>

      <div className="field" style={{ marginTop: 10 }}>
        <label>Vencimiento</label>
        <input
          type="date"
          value={expiry || ""}
          onChange={(e) => onExpiryChange(e.target.value)}
        />
      </div>
    </div>
  );
}

function SupplierViewModal({
  supplier,
  onClose,
  onSavePartial,
  projectCode,
  setToast,
}) {
  const [tab, setTab] = useState("info"); // info | pagos | contacto | certs
  const [local, setLocal] = useState(supplier);

  useEffect(() => {
    setLocal(supplier);
  }, [supplier]);

  const handleUpload = async (file, kind) => {
    if (!file) return;
    const id = supplier.id;
    const path = `projects/${projectCode}/suppliers/${id}/${kind}/${Date.now()}_${
      file.name
    }`;
    const ref = storageRef(storage, path);
    await uploadBytes(ref, file);
    const url = await getDownloadURL(ref);

    const patch =
      kind === "certBank"
        ? {
            certBank: {
              ...(local.certBank || {}),
              fileUrl: url,
              fileName: file.name,
              uploadedAt: new Date().toISOString(),
            },
          }
        : {
            certAeat: {
              ...(local.certAeat || {}),
              fileUrl: url,
              fileName: file.name,
              uploadedAt: new Date().toISOString(),
            },
          };

    const newLocal = { ...local, ...patch };
    setLocal(newLocal);
    await onSavePartial(newLocal);
    setToast?.({ msg: "Certificado subido correctamente" });
  };

  const handleRemove = async (kind) => {
    const cur =
      kind === "certBank" ? local.certBank?.fileUrl : local.certAeat?.fileUrl;
    if (cur) {
      try {
        const ref = storageRef(storage, cur);
      } catch {}
    }
    const patch =
      kind === "certBank"
        ? { certBank: { ...local.certBank, fileUrl: "", fileName: "" } }
        : { certAeat: { ...local.certAeat, fileUrl: "", fileName: "" } };

    const newLocal = { ...local, ...patch };
    setLocal(newLocal);
    await onSavePartial(newLocal);
    setToast?.({ msg: "Certificado eliminado" });
  };

  const saveExpiry = async (kind, value) => {
    const patch =
      kind === "certBank"
        ? { certBank: { ...(local.certBank || {}), expiry: value } }
        : { certAeat: { ...(local.certAeat || {}), expiry: value } };
    const newLocal = { ...local, ...patch };
    setLocal(newLocal);
    await onSavePartial(newLocal);
    setToast?.({ msg: "Vencimiento actualizado" });
  };

  return (
    <Modal title="Proveedor" onClose={onClose} style={{ maxWidth: 980 }}>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        {["info", "pagos", "contacto", "certs"].map((t) => (
          <button
            key={t}
            className={`button ${t === tab ? "button--primary" : ""}`}
            onClick={() => setTab(t)}
          >
            {t === "info" && "Información"}
            {t === "pagos" && "Pagos"}
            {t === "contacto" && "Contacto"}
            {t === "certs" && "Certificados"}
          </button>
        ))}
      </div>

      {tab === "info" && (
        <div className="stack" style={{ gap: 8 }}>
          <div>
            <strong>Nombre:</strong> {local.name || "—"}
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div>
              <strong>Tipo:</strong> <BadgeType type={local.type} />
            </div>
            <div>
              <strong>CIF:</strong> {local.cif || "—"}
            </div>
          </div>
          <div>
            <strong>Dirección:</strong> {local.address || "—"}
          </div>
          <div>
            <strong>Localidad:</strong> {local.city || "—"} •{" "}
            <strong>Provincia:</strong> {local.province || "—"} •{" "}
            <strong>CP:</strong> {local.postalCode || "—"} •{" "}
            <strong>País:</strong> {local.country || "—"}
          </div>
          <div>
            <strong>Web:</strong>{" "}
            {local.website ? (
              <a href={local.website} target="_blank" rel="noreferrer">
                {local.website}
              </a>
            ) : (
              "—"
            )}
          </div>
        </div>
      )}

      {tab === "pagos" && (
        <div className="stack" style={{ gap: 8 }}>
          <div>
            <strong>Método:</strong> {local.paymentMethod || "—"}
          </div>
          <div>
            <strong>IBAN / Nº cuenta:</strong>{" "}
            <span
              style={{
                color:
                  local.iban && !validateIBAN(local.iban)
                    ? "#dc2626"
                    : "inherit",
                fontWeight: 500,
              }}
              title={
                local.iban
                  ? validateIBAN(local.iban)
                    ? "IBAN válido"
                    : "Posible IBAN no válido"
                  : "—"
              }
            >
              {local.iban || "—"}
              {local.iban && !validateIBAN(local.iban) && (
                <>
                  {" "}
                  &nbsp;
                  <FileWarning
                    size={16}
                    style={{ verticalAlign: "text-bottom" }}
                  />
                </>
              )}
            </span>
          </div>
        </div>
      )}

      {tab === "contacto" && (
        <div className="stack" style={{ gap: 8 }}>
          <div>
            <strong>Contacto:</strong> {local.contactName || "—"}
          </div>
          <div>
            <strong>Email:</strong> {local.contactEmail || "—"}
          </div>
          <div>
            <strong>Teléfono:</strong> {local.contactPhone || "—"}
          </div>
        </div>
      )}

      {tab === "certs" && (
        <div className="form-grid">
          <CertDropzone
            label="Certificado bancario"
            value={local.certBank}
            expiry={local?.certBank?.expiry || ""}
            onFileSelected={(f) => handleUpload(f, "certBank")}
            onRemoveFile={() => handleRemove("certBank")}
            onExpiryChange={(v) => saveExpiry("certBank", v)}
          />
          <CertDropzone
            label="Certificado AEAT"
            value={local.certAeat}
            expiry={local?.certAeat?.expiry || ""}
            onFileSelected={(f) => handleUpload(f, "certAeat")}
            onRemoveFile={() => handleRemove("certAeat")}
            onExpiryChange={(v) => saveExpiry("certAeat", v)}
          />
        </div>
      )}
    </Modal>
  );
}

function SupplierFormModal({
  open,
  editMode,
  form,
  setForm,
  onClose,
  onSubmit,
  formStep,
  setFormStep,
}) {
  if (!open) return null;

  return (
    <Modal
      title={editMode ? "Editar proveedor" : "Nuevo proveedor"}
      onClose={onClose}
      style={{ maxWidth: "900px" }}
    >
      <form className="stack" onSubmit={onSubmit}>
        {formStep === 1 && (
          <div className="form-grid">
            <div className="field field--span2">
              <label>Nombre fiscal</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="field">
              <label>Tipo</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="ES">ES</option>
                <option value="EU">EU</option>
                <option value="INT">Internacional</option>
              </select>
            </div>
            <div className="field">
              <label>CIF</label>
              <input
                type="text"
                value={form.cif}
                onChange={(e) => setForm({ ...form, cif: e.target.value })}
                required
              />
              {form.cif && !validateCIFBasic(form.cif) && (
                <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                  Formato de CIF/NIF posiblemente no válido.
                </div>
              )}
            </div>
            <div className="field field--span2">
              <label>Dirección</label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div className="field">
              <label>CP</label>
              <input
                type="text"
                value={form.postalCode}
                onChange={(e) =>
                  setForm({ ...form, postalCode: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>Población</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Provincia</label>
              <input
                type="text"
                value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
              />
            </div>
            <div className="field">
              <label>País</label>
              <input
                type="text"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
              />
            </div>
          </div>
        )}

        {formStep === 2 && (
          <div className="form-grid">
            <div className="field">
              <label>Método de pago</label>
              <select
                value={form.paymentMethod}
                onChange={(e) =>
                  setForm({ ...form, paymentMethod: e.target.value })
                }
              >
                <option value="transferencia">Transferencia</option>
                <option value="domiciliación">Domiciliación</option>
                <option value="tarjeta">Tarjeta</option>
              </select>
            </div>
            <div className="field field--span2">
              <label>IBAN / Nº cuenta</label>
              <input
                type="text"
                value={form.iban}
                onChange={(e) => setForm({ ...form, iban: e.target.value })}
              />
              {form.iban && !validateIBAN(form.iban) && (
                <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                  IBAN posiblemente no válido.
                </div>
              )}
            </div>
          </div>
        )}

        {formStep === 3 && (
          <div className="form-grid">
            <div className="field field--span2">
              <label>Nombre de contacto</label>
              <input
                type="text"
                value={form.contactName}
                onChange={(e) =>
                  setForm({ ...form, contactName: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                value={form.contactEmail}
                onChange={(e) =>
                  setForm({ ...form, contactEmail: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>Teléfono</label>
              <input
                type="tel"
                value={form.contactPhone}
                onChange={(e) =>
                  setForm({ ...form, contactPhone: e.target.value })
                }
              />
            </div>
            <div className="field field--span2">
              <label>Web</label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 16,
          }}
        >
          {formStep > 1 && (
            <button
              type="button"
              className="button"
              onClick={() => setFormStep(formStep - 1)}
            >
              ← Atrás
            </button>
          )}
          {formStep < 3 ? (
            <button
              type="button"
              className="button button--primary"
              style={{ marginLeft: "auto" }}
              onClick={() => setFormStep(formStep + 1)}
            >
              Siguiente →
            </button>
          ) : (
            <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
              <button type="button" className="button" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="button button--primary">
                Guardar
              </button>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

// ===== 4. Componente Principal =====
export default function SuppliersPage({ projectCode }) {
  // Estados
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [formStep, setFormStep] = useState(1);
  const [form, setForm] = useState(initialForm());
  const [toast, setToast] = useState(null);

  // Filtros / búsqueda / paginación
  const [query, setQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("all"); // all | ok | missing | expired | near
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Sync Firestore
  useEffect(() => {
    if (!projectCode) return;
    const unsub = onSnapshot(
      collection(db, "projects", projectCode, "suppliers"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // ordenar por code asc
        list.sort((a, b) => (a.code || 0) - (b.code || 0));
        setSuppliers(list);
      }
    );
    return () => unsub();
  }, [projectCode]);

  // Filter + search
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = suppliers;
    if (q) {
      list = list.filter(
        (s) =>
          s.name?.toLowerCase().includes(q) || s.cif?.toLowerCase().includes(q)
      );
    }
    if (stateFilter !== "all") {
      list = list.filter((s) => {
        const st = computeCertStatus(s);
        if (stateFilter === "ok") return st.level === "green";
        if (stateFilter === "expired") return st.level === "red";
        if (stateFilter === "missing")
          return !s?.certBank?.fileUrl || !s?.certAeat?.fileUrl;
        if (stateFilter === "near")
          return isNear(s?.certBank?.expiry) || isNear(s?.certAeat?.expiry);
        return true;
      });
    }
    return list;
  }, [suppliers, query, stateFilter]);

  // Paginación simple
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  // Guardar / Actualizar
  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.name || !form.cif) {
      alert("El nombre y CIF son obligatorios");
      return;
    }

    let newCode = 1;
    if (!editMode) {
      const codes = suppliers
        .map((s) => s.code)
        .filter((c) => typeof c === "number");
      if (codes.length > 0) {
        newCode = Math.max(...codes) + 1;
      }
    }

    const supplierId = editMode || form.name.trim();

    try {
      const payload = {
        ...form,
        code: editMode ? form.code : newCode,
        updatedAt: serverTimestamp(),
        createdAt: editMode ? form.createdAt : serverTimestamp(),
      };
      await setDoc(
        doc(db, "projects", projectCode, "suppliers", supplierId),
        payload
      );
      setToast({
        msg: editMode
          ? "Proveedor actualizado correctamente"
          : "Proveedor creado correctamente",
      });
      resetForm();
    } catch (err) {
      console.error("Error guardando proveedor:", err);
      setToast({ msg: "Error al guardar proveedor", error: true });
    }
  };

  const resetForm = () => {
    setForm(initialForm());
    setEditMode(null);
    setFormStep(1);
    setShowForm(false);
    setSelectedSupplier(null);
  };

  const handleDelete = async (supplier) => {
    if (!window.confirm("¿Seguro que deseas eliminar este proveedor?")) return;
    try {
      await deleteDoc(
        doc(db, "projects", projectCode, "suppliers", supplier.id)
      );
      setToast({ msg: "Proveedor eliminado correctamente" });
      setSelectedSupplier(null);
    } catch (err) {
      console.error("Error eliminando proveedor:", err);
      setToast({ msg: "Error al eliminar proveedor", error: true });
    }
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (s) => {
    setForm({ ...initialForm(), ...s });
    setEditMode(s.id);
    setFormStep(1);
    setShowForm(true);
  };

  const onView = (s) => setSelectedSupplier(s);

  const savePartial = async (partial) => {
    // Guarda todo el supplier local (parcial) para mantener consistencia
    try {
      const ref = doc(db, "projects", projectCode, "suppliers", partial.id);
      await setDoc(
        ref,
        { ...partial, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.error(e);
      setToast({ msg: "No se pudo actualizar el proveedor", error: true });
    }
  };

  // ===== Render =====
  return (
    <section className="container" style={{ padding: "20px 0 30px" }}>
      {/* ===== TOOLBAR SUPERIOR COMPACTA ===== */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          marginTop: 4,
          marginBottom: 16,
        }}
      >
        <button
          className="button button--primary"
          title="Nuevo proveedor"
          onClick={openCreate}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            fontSize: 13,
            height: 32,
          }}
        >
          <PlusCircle size={15} />
          <span>Nuevo proveedor</span>
        </button>
      </div>

      {/* ===== CARD CENTRAL: BÚSQUEDA Y FILTROS ===== */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 24,
        }}
      >
        <div
          className="card"
          style={{
            display: "flex",
            gap: 16,
            padding: 20,
            border: "1px solid var(--ring)",
            borderRadius: 12,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {/* Buscador */}
          <div
            className="field"
            style={{ margin: 0, minWidth: 260 }}
            title="Buscar por nombre o CIF"
          >
            <input
              type="search"
              placeholder="Buscar por nombre o CIF"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {/* Filtro */}
          <div className="field" style={{ margin: 0 }}>
            <select
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(e.target.value);
                setPage(1);
              }}
              title="Filtrar por estado de certificados"
            >
              <option value="all">Todos</option>
              <option value="ok">OK</option>
              <option value="missing">Faltan certificados</option>
              <option value="near">Próximos a vencer</option>
              <option value="expired">Vencidos</option>
            </select>
          </div>
        </div>
      </div>

      {/* ===== LISTADO ===== */}
      <div className="stack">
        {pageItems.map((s) => (
          <SupplierCard
            key={s.id}
            s={s}
            onView={onView}
            onEdit={openEdit}
            onDelete={handleDelete}
          />
        ))}

        {filtered.length === 0 && (
          <p style={{ color: "var(--muted)" }}>
            No hay proveedores que coincidan con el criterio.
          </p>
        )}
      </div>

      {/* ===== PAGINACIÓN ===== */}
      {filtered.length > 0 && (
        <div
          style={{
            marginTop: 16,
            display: "flex",
            justifyContent: "center",
            gap: 12,
            alignItems: "center",
          }}
        >
          <button
            className="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            title="Anterior"
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 14 }}>
            Página {page} / {totalPages}
          </span>
          <button
            className="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            title="Siguiente"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* ===== MODALES ===== */}
      <SupplierFormModal
        open={showForm}
        editMode={editMode}
        form={form}
        setForm={setForm}
        onClose={resetForm}
        onSubmit={handleSave}
        formStep={formStep}
        setFormStep={setFormStep}
      />

      {selectedSupplier && (
        <SupplierViewModal
          supplier={selectedSupplier}
          projectCode={projectCode}
          onClose={() => setSelectedSupplier(null)}
          onSavePartial={async (partial) => {
            await savePartial(partial);
            setSelectedSupplier(partial);
          }}
          setToast={setToast}
        />
      )}

      {/* ===== TOAST ===== */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            background: toast.error ? "#dc2626" : "var(--accent)",
            color: "#fff",
            padding: "12px 20px",
            borderRadius: 10,
          }}
          role="status"
        >
          {toast.msg}
        </div>
      )}
    </section>
  );
}
